import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { LifeBuoy, CheckCircle2, Sparkles, Mail, Loader2, Send } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Mein Mikrofon funktioniert nicht in der Simulation.",
    a: "Bitte erlauben Sie den Mikrofon-Zugriff im Browser und laden Sie die Seite neu. Empfohlen: Chrome oder Edge, aktuelle Version. Prüfen Sie auch die Systemeinstellungen Ihres Betriebssystems.",
  },
  {
    q: "Die KI antwortet verzögert.",
    a: "In der Alpha kann es bei hoher Last kurze Latenzen geben. Falls Sie > 3 Sekunden warten, versuchen Sie eine erneute Simulation oder melden Sie den Zeitpunkt über das Formular.",
  },
  {
    q: "Mein Score scheint nicht zu passen.",
    a: "Feedback zur Score-Logik ist besonders wertvoll. Bitte beschreiben Sie das Szenario, Ihre Antworten und den erwarteten Score — wir justieren die Bewertung kontinuierlich.",
  },
  {
    q: "Ich kann mich nicht einloggen.",
    a: "Setzen Sie ihr Passwort zurück oder nutzen Sie die Google-Anmeldung. Falls das Problem bestehen bleibt, senden Sie uns ein Ticket unter 'Login- / Zugriffsprobleme'.",
  },
  {
    q: "Werden meine Daten gespeichert?",
    a: "Wir speichern nur, was für Score und Trainer-Dashboard nötig ist — DSGVO-konform, EU-gehostet. Details in unseren Datenschutzhinweisen.",
  },
];

export default function Support() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "",
    subject: "",
    description: "",
    screenshot_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // ticket id

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/support/categories");
        setCategories(data);
      } catch (_e) {
        /* fallback */
        setCategories([
          { value: "bug", label: "Bug / Fehler in der App" },
          { value: "login", label: "Login- / Zugriffsprobleme" },
          { value: "feedback", label: "Feedback zur Alpha" },
          { value: "feature", label: "Feature-Wunsch" },
          { value: "other", label: "Sonstiges" },
        ]);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Bitte wählen Sie eine Kategorie.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/ticket", {
        ...form,
        screenshot_url: form.screenshot_url?.trim() || null,
      });
      setSubmitted(data.ticket_id);
      toast.success("Ticket erfolgreich erstellt. Wir melden uns zeitnah.");
      setForm({ name: "", email: "", category: "", subject: "", description: "", screenshot_url: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FCFCFB]" data-testid="support-page">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-[#EAEBE6] bg-[#F4F5F2]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#FCEBD6] text-[#B36B15] tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Alpha · Early Tester
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-semibold text-[#5A5B5C] tracking-tight leading-[1.05] max-w-3xl">
            Fehler gefunden? Danke — genau dafür gibt’s die Alpha.
          </h1>
          <p className="mt-5 text-lg text-[#272525] max-w-2xl leading-relaxed">
            Hier melden Sie Bugs, Login-Probleme oder teilen Ideen. Wir antworten in der Regel
            innerhalb eines Werktags.{" "}
            <span className="text-[#5A5B5C] font-medium">Bald direkt verbunden mit Zendesk.</span>
          </p>
        </div>
      </section>

      {/* Main grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#EAEBE6] rounded-2xl p-6 sm:p-10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                  <LifeBuoy className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-semibold text-[#5A5B5C]">Ticket erstellen</h2>
              </div>

              {submitted ? (
                <div className="mt-8 rounded-xl border border-[#A7B584]/40 bg-[#F4F5F2]/60 p-6" data-testid="support-success">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#A7B584] shrink-0" />
                    <div>
                      <div className="font-semibold text-[#5A5B5C]">Ticket erfolgreich eingereicht</div>
                      <p className="mt-1.5 text-sm text-[#272525]">
                        Ihre Ticket-ID: <span className="font-mono text-[#5A5B5C]">{submitted}</span>
                      </p>
                      <p className="mt-3 text-sm text-[#272525]">
                        Wir melden uns per E-Mail. Bei dringenden Problemen nennen Sie diese ID in Ihrer Antwort.
                      </p>
                      <button
                        className="mt-5 text-sm font-semibold text-[#A7B584] hover:text-[#8C9A69]"
                        onClick={() => setSubmitted(null)}
                        data-testid="support-new-ticket"
                      >
                        Weiteres Ticket erstellen
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="support-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name" htmlFor="s-name">
                      <Input
                        id="s-name"
                        required
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        data-testid="support-name-input"
                        className="h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
                      />
                    </Field>
                    <Field label="E-Mail" htmlFor="s-email">
                      <Input
                        id="s-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        data-testid="support-email-input"
                        className="h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
                      />
                    </Field>
                  </div>

                  <Field label="Kategorie" htmlFor="s-cat">
                    <Select value={form.category} onValueChange={(v) => set("category", v)}>
                      <SelectTrigger
                        id="s-cat"
                        data-testid="support-category-trigger"
                        className="h-11 border-[#EAEBE6] rounded-lg focus:ring-[#A7B584]"
                      >
                        <SelectValue placeholder="Bitte wählen…" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.value} value={c.value} data-testid={`support-cat-${c.value}`}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Betreff" htmlFor="s-subject">
                    <Input
                      id="s-subject"
                      required
                      value={form.subject}
                      onChange={(e) => set("subject", e.target.value)}
                      data-testid="support-subject-input"
                      className="h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
                    />
                  </Field>

                  <Field label="Beschreibung" htmlFor="s-desc">
                    <Textarea
                      id="s-desc"
                      required
                      rows={6}
                      placeholder="Was ist passiert? Welches Szenario? Welche Schritte haben zum Fehler geführt?"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      data-testid="support-description-input"
                      className="border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
                    />
                  </Field>

                  <Field label="Screenshot-URL (optional)" htmlFor="s-shot">
                    <Input
                      id="s-shot"
                      type="url"
                      placeholder="https://…"
                      value={form.screenshot_url}
                      onChange={(e) => set("screenshot_url", e.target.value)}
                      data-testid="support-screenshot-input"
                      className="h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
                    />
                  </Field>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="support-submit-btn"
                    className="bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg px-6 h-12 font-semibold"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Ticket senden</>}
                  </Button>
                </form>
              )}
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-[#1E1F1A] text-white rounded-2xl p-6 sm:p-8">
              <Mail className="h-5 w-5 text-[#A7B584]" />
              <div className="mt-4 text-xl font-semibold">Direktkontakt</div>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">
                Lieber per E-Mail? Schreiben Sie uns — in der Alpha antworten Gründer:innen persönlich.
              </p>
              <a
                href="mailto:support@help4emergency.de"
                className="mt-4 inline-flex items-center gap-2 text-[#A7B584] hover:text-white font-semibold"
                data-testid="support-email-link"
              >
                support@help4emergency.de
              </a>
              <p className="mt-6 text-xs text-white/50">
                Zendesk-Integration folgt — bis dahin landen Tickets sicher in unserer Alpha-Queue.
              </p>
            </div>

            <div className="bg-white border border-[#EAEBE6] rounded-2xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
                Häufige Fragen
              </div>
              <h3 className="mt-2 text-xl font-semibold text-[#5A5B5C]">Schnellhilfe</h3>
              <Accordion type="single" collapsible className="mt-4" data-testid="support-faq">
                {FAQ_ITEMS.map((item, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#EAEBE6] last:border-0">
                    <AccordionTrigger
                      className="text-left text-[#5A5B5C] font-semibold hover:no-underline py-4 text-sm"
                      data-testid={`support-faq-${i}`}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#272525] text-sm leading-relaxed pb-4">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-[#5A5B5C] text-sm font-semibold">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
